import { type StoreInfoRequestContext } from '../context';

const QUERY_PARAMS = {
    isBanned: 'dev_store_is_banned',
    domainBlockReason: 'dev_store_domain_block_reason',
    canRequestDomainRemoderation: 'dev_store_can_request_domain_remoderation',
    hasCompanyNameModerationError: 'dev_company_name_moderation_error',
} as const;

export type StoreModerationOverrides = {
    isBanned?: boolean;
    domainBlockReason?: string;
    canRequestDomainRemoderation?: boolean;
    hasCompanyNameModerationError?: boolean;
};

const parseBoolean = (value: string | null): boolean | undefined => {
    if (value === 'true') {
        return true;
    }

    if (value === 'false') {
        return false;
    }

    return;
};

export const parseStoreModerationOverrides = (searchParams: URLSearchParams): StoreModerationOverrides => {
    const overrides: StoreModerationOverrides = {};
    const isBanned = parseBoolean(searchParams.get(QUERY_PARAMS.isBanned));
    const canRequestDomainRemoderation = parseBoolean(searchParams.get(QUERY_PARAMS.canRequestDomainRemoderation));
    const hasCompanyNameModerationError = parseBoolean(searchParams.get(QUERY_PARAMS.hasCompanyNameModerationError));

    if (isBanned !== undefined) {
        overrides.isBanned = isBanned;
    }

    if (searchParams.has(QUERY_PARAMS.domainBlockReason)) {
        overrides.domainBlockReason = searchParams.get(QUERY_PARAMS.domainBlockReason) ?? '';
    }

    if (canRequestDomainRemoderation !== undefined) {
        overrides.canRequestDomainRemoderation = canRequestDomainRemoderation;
    }

    if (hasCompanyNameModerationError !== undefined) {
        overrides.hasCompanyNameModerationError = hasCompanyNameModerationError;
    }

    return overrides;
};

export const applyStoreModerationOverrides = (
    context: StoreInfoRequestContext,
    overrides: StoreModerationOverrides
): StoreInfoRequestContext => {
    return {
        ...context,
        ...(overrides.isBanned !== undefined && { isBanned: overrides.isBanned }),
        ...(overrides.domainBlockReason !== undefined && {
            domainBlockReasons: overrides.domainBlockReason ? [overrides.domainBlockReason] : [],
        }),
        ...(overrides.canRequestDomainRemoderation !== undefined && {
            canRequestDomainRemoderation: overrides.canRequestDomainRemoderation,
        }),
    };
};

export const resolveStoreModerationContext = (
    context: StoreInfoRequestContext,
    searchParams: URLSearchParams,
    canUseDevOverrides: boolean
): StoreInfoRequestContext =>
    canUseDevOverrides ? applyStoreModerationOverrides(context, parseStoreModerationOverrides(searchParams)) : context;

export const getLocalStoreModerationOverrides = (): StoreModerationOverrides => {
    if (typeof window === 'undefined') {
        return {};
    }

    return parseStoreModerationOverrides(new URL(window.location.href).searchParams);
};

const setOptionalParam = (searchParams: URLSearchParams, name: string, value: boolean | string | undefined) => {
    if (value === undefined) {
        searchParams.delete(name);
    } else {
        searchParams.set(name, String(value));
    }
};

export const setLocalStoreModerationOverrides = (overrides: StoreModerationOverrides): void => {
    if (typeof window === 'undefined') {
        return;
    }

    const url = new URL(window.location.href);

    setOptionalParam(url.searchParams, QUERY_PARAMS.isBanned, overrides.isBanned);
    setOptionalParam(url.searchParams, QUERY_PARAMS.domainBlockReason, overrides.domainBlockReason);
    setOptionalParam(
        url.searchParams,
        QUERY_PARAMS.canRequestDomainRemoderation,
        overrides.canRequestDomainRemoderation
    );
    setOptionalParam(
        url.searchParams,
        QUERY_PARAMS.hasCompanyNameModerationError,
        overrides.hasCompanyNameModerationError
    );

    window.history.replaceState(null, '', url);
};

export const hasStoreModerationOverrides = (overrides: StoreModerationOverrides): boolean =>
    Object.values(overrides).some((value) => value !== undefined);
